import type { DeepPartial } from 'typeorm';
import type { ObjectFieldSelectOptionsEntity } from '../models';
import type { PromisifyRelations } from '../types';

export function formatTitleCase(str: string): string {
	if (str[0] == null) {
		return str;
	}

	// from snake_case
	str = str.replace(/_/g, ' ');

	// from spinal-case
	if (!str.includes(' ')) {
		str = str.replace(/-/g, ' ');
	}

	// from camelCase
	str = str.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

	const words = str
		.toLowerCase()
		.split(' ')
		.filter((word) => word.length > 0);

	return words
		.map((word) => `${word[0]?.toUpperCase()}${word.slice(1)}`)
		.join(' ');
}

/**
 * TH 2025-11-13: If the values are non-numeric strings, use them as the text.
 * Otherwise convert the key to title case and use it as the text.
 */
export function formatEnumAsSelectOptions(enumValue: {
	[key: string]: unknown;
}): Array<
	DeepPartial<ObjectFieldSelectOptionsEntity> &
		DeepPartial<PromisifyRelations<ObjectFieldSelectOptionsEntity>>
> {
	return Object.entries(enumValue).map(([text, value]) => {
		if (typeof value !== 'number' && typeof value !== 'string') {
			throw new Error(`Invalid enum type: ${typeof value}`);
		}

		if (typeof value !== 'string' || value === `${parseInt(value, 10)}`) {
			text = formatTitleCase(text);
		} else {
			text = value;
		}

		return {
			value: `${value}`,
			text,
			subtext: null,
		};
	});
}

export function formatFullName(
	names: Array<string | undefined | null>,
): string | undefined {
	let formattedName: string | undefined;

	for (const name of names) {
		if (name == null || name.trim() === '') {
			continue;
		}

		formattedName ??= '';
		formattedName += ` ${name.trim()}`;
	}

	return formattedName?.trim();
}

/**
 * Values that are the ABSENCE of a value written down as text. They reach the
 * database through imports and older forms that stringified a null before
 * storing it — Auvis has a client whose payment terms are the four characters
 * "null", which printed as "Payment terms: null" on a customer's order email.
 * A record holding one of these has no value, so it is treated as empty.
 */
const NULLISH_TEXT_VALUES = new Set(['null', 'undefined', 'nan']);

export function formatText(
	str: string | number | boolean | null | undefined,
	defaultIfEmpty: string = '-',
): string {
	if (typeof str === 'number') {
		str = `${str}`;
	}

	if (typeof str === 'boolean') {
		str = str ? 'Yes' : 'No';
	}

	if (str == null) {
		return defaultIfEmpty;
	}

	const trimmed = str.trim();

	if (trimmed === '' || NULLISH_TEXT_VALUES.has(trimmed.toLowerCase())) {
		return defaultIfEmpty;
	}

	return str;
}

/**
 * Formats the provided word as singular or plural, depending on the count. Also
 * allows providing a following verb that will also be formatted based on the
 * plurality.
 *
 * @example
 * formatPlural(2, 'invoice'); // 2 invoices
 * formatPlural(2, 'box', { plural: 'boxes' }); // 2 boxes
 * formatPlural(1, 'error', { followingVerb: 'need' }) + ' attention'; // 1 error needs attention
 * formatPlural(2, 'error', { followingVerb: 'need' }) + ' attention'; // 2 errors need attention
 * formatPlural(1, 'question', { followingVerb: 'are', followingVerbIfSingular: 'is' }) + ' remaining'; // 1 question is remaining
 * formatPlural(2, 'question', { followingVerb: 'are', followingVerbIfSingular: 'is' }) + ' remaining'; // 1 questions are remaining
 */
export function formatPlural(
	count: number,
	singular: string,
	options: {
		plural?: string;
		followingVerb?: string;
		followingVerbIfSingular?: string;
	} = {},
): string {
	let result: string;

	if (count === 1) {
		result = `${count} ${singular}`;

		if (options.followingVerb != null) {
			options.followingVerbIfSingular ??= `${options.followingVerb}s`;
		}

		if (options.followingVerbIfSingular != null) {
			result += ` ${options.followingVerbIfSingular}`;
		}
	} else {
		options.plural ??= `${singular}s`;
		result = `${count} ${options.plural}`;

		if (options.followingVerb != null) {
			result += ` ${options.followingVerb}`;
		}
	}

	return result;
}
